import { toInteger } from 'lodash';
import { createCanvas } from '@/utils/file-kit';
import type { IBrushLayerProps, ILayerProps } from '@/version-types';
import { BrushCreator } from '../utils/brush-creator';

/**
 * 对画笔画布应用图层变换（翻转、旋转、缩放、偏移）并可选地应用透明度
 */
const applyLayerTransform = (
    layer: ILayerProps,
    baseImage: HTMLImageElement | HTMLCanvasElement,
    opacity = 1,
    renderScale = 1,
) => {
    const { width, height } = layer;
    const {
        imageTransform: {
            flipH,
            flipV,
            scale,
            rotate: imgRotate,
            widthRatio,
            heightRatio,
            offsetX: offsetXRatio,
            offsetY: offsetYRatio,
        },
    } = layer.data as IBrushLayerProps;

    const resultWidth = Math.max(1, toInteger(width * renderScale));
    const resultHeight = Math.max(1, toInteger(height * renderScale));
    const offsetX = toInteger(offsetXRatio * widthRatio * width * scale * renderScale);
    const offsetY = toInteger(offsetYRatio * heightRatio * height * scale * renderScale);
    const originImgWidth = Math.max(1, toInteger(width * widthRatio * renderScale));
    const originImgHeight = Math.max(1, toInteger(height * heightRatio * renderScale));

    let imgOffsetX = resultWidth / 2;
    let imgOffsetY = resultHeight / 2;

    if (flipH) {
        imgOffsetX = imgOffsetX - offsetX;
    } else {
        imgOffsetX = imgOffsetX + offsetX;
    }

    if (flipV) {
        imgOffsetY = imgOffsetY - offsetY;
    } else {
        imgOffsetY = imgOffsetY + offsetY;
    }

    let rotate = imgRotate;
    if (flipH) {
        rotate = -rotate;
    }
    if (flipV) {
        rotate = -rotate;
    }

    const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);

    destCtx.globalAlpha = opacity;
    destCtx.translate(imgOffsetX, imgOffsetY);
    destCtx.rotate((rotate * Math.PI) / 180);
    destCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
    destCtx.drawImage(baseImage, -originImgWidth / 2, -originImgHeight / 2, originImgWidth, originImgHeight);

    return destCanvas;
};

/**
 * 将画笔图层内容渲染到 Canvas
 *
 * @param layer - 图层属性
 * @param identity - 框架实例标识
 * @param globalAlpha - 是否应用透明度，默认 true
 * @param scale - 渲染缩放比例，默认 1
 */
export const renderLayerContentToCanvas = async (
    layer: ILayerProps,
    identity: string,
    globalAlpha = true,
    scale = 1,
): Promise<HTMLCanvasElement> => {
    const { opacity } = layer;

    const baseImage = await BrushCreator.create(layer, identity, scale);

    const layerCanvas = applyLayerTransform(layer, baseImage, globalAlpha ? opacity : 1, scale);
    return layerCanvas;
};
